import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CandlestickChart as ChartIcon, Bell, LogOut, User, Settings2, LifeBuoy, BadgeCheck, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { apiPost as apiPostRaw } from "@/lib/api";
import { apiGet, apiPost, apiPatch, fmt } from "@/lib/api";
import { useNow, fmtClock, fmtDateTime } from "@/lib/terminalUtils";
import { SYMBOL_LIST } from "@/lib/symbols";
import { OverviewPanel } from "@/components/terminal/OverviewPanel";
import { StructurePanel, LiquidityPanel } from "@/components/terminal/SidePanels";
import { CandleChart } from "@/components/terminal/CandleChart";
import { SetupCard } from "@/components/terminal/SetupCard";
import { LiveMonitor } from "@/components/terminal/LiveMonitor";
import { WatchlistTable } from "@/components/terminal/WatchlistTable";
import { AlertsTable } from "@/components/terminal/AlertsTable";
import { PositionsPanel } from "@/components/terminal/PositionsPanel";
import { SignalCard } from "@/components/platform/SignalCard";
import { SettingsPanel } from "@/components/platform/SettingsPanel";
import { BrokerPanel } from "@/components/platform/BrokerPanel";
import { TelegramPanel } from "@/components/terminal/TelegramPanel";
import { SourceBar } from "@/components/terminal/SourceBar";
import { HistoryPage } from "@/components/terminal/HistoryPage";
import { LearningPage } from "@/components/terminal/LearningPage";
import { ExecutionLab } from "@/components/terminal/ExecutionLab";
import { PnlPage } from "@/components/terminal/PnlPage";
import { NewsPage } from "@/components/terminal/NewsPage";
import { AdminTab } from "@/components/terminal/AdminTab";
import { SupportChat } from "@/components/terminal/SupportChat";
import { AccessBar } from "@/components/terminal/AccessBar";
import { PlansPanel } from "@/components/terminal/PlansPanel";
import { PushPanel } from "@/components/terminal/PushPanel";
import { LiquidityFlow } from "@/components/terminal/LiquidityFlow";

const TABS = [
  ["dashboard", "Dashboard"],
  ["chart", "Chart"],
  ["liquidity-flow", "Liquidity Flow"],
  ["scanner", "Scanner"],
  ["execution", "Execution Lab"],
  ["learning", "Learning"],
  ["history", "History"],
  ["pnl", "PnL"],
  ["news", "News"],
  ["settings", "Settings"],
];

const Panel = ({ children, className = "" }) => (
  <div className={`border border-white/10 bg-[#0d1219] ${className}`}>{children}</div>
);

export default function Terminal() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [symbol, setSymbol] = useState("XAUUSD");
  const [notifs, setNotifs] = useState(null);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [savingProfileName, setSavingProfileName] = useState(false);
  const [notificationPage, setNotificationPage] = useState(1);
  const NOTIFICATIONS_PER_PAGE = 10;
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [vcode, setVcode] = useState("");
  const navigate = useNavigate();
  const timer = useRef(null);
  const generalNotifIdsRef = useRef(new Set());
  const generalNotifInitializedRef = useRef(false);
  const now = useNow();

  const [branding, setBranding] = useState({ brand_name: "NAKED CHART AI", logo_url: "", tagline: "" });

  useEffect(() => {
    apiGet("/branding")
      .then(({ data }) => setBranding(data || {}))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await apiGet("/platform/overview");
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("atr_token");
        navigate("/masuk");
      }
    }
  }, [navigate]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [load]);

  useEffect(() => {
    let active = true;
    const pollGeneralNotifications = async () => {
      try {
        const { data } = await apiGet("/notifications");
        if (!active) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const generalItems = items.filter((n) => n?.type === "general");
        if (!generalNotifInitializedRef.current) {
          generalItems.forEach((n) => { const id = n?.id || n?._id; if (id) generalNotifIdsRef.current.add(id); });
          generalNotifInitializedRef.current = true;
          return;
        }
        for (const n of generalItems) {
          const id = n?.id || n?._id;
          if (!id || generalNotifIdsRef.current.has(id)) continue;
          generalNotifIdsRef.current.add(id);
          toast(n?.title || "NAKED CHART", { description: n?.message || "Ada notifikasi baru." });
        }
      } catch (err) { console.debug("General notification polling:", err); }
    };
    pollGeneralNotifications();
    const generalTimer = setInterval(pollGeneralNotifications, 5000);
    return () => { active = false; clearInterval(generalTimer); };
  }, []);

  useEffect(() => {
    if (data?.user?.name != null) {
      setProfileName(data.user.name || "");
    }
  }, [data?.user?.name]);

  const [supportUnread, setSupportUnread] = useState(0);
  useEffect(() => {
    if (data?.user?.role !== "admin") return;
    let alive = true;
    const poll = () => apiGet("/admin/support/unread")
      .then((r) => alive && setSupportUnread(r.data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [data?.user?.role]);

  const saveProfileName = async () => {
    const name = profileName.trim();

    if (!name) {
      toast.error("Nama tidak boleh kosong.");
      return;
    }

    setSavingProfileName(true);

    try {
      const { data: result } = await apiPatch("/profile/name", { name });

      setData((prev) =>
        prev?.user
          ? { ...prev, user: { ...prev.user, name: result.name } }
          : prev
      );

      setProfileName(result.name || "");
      toast.success("Nama berhasil diperbarui.");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Gagal memperbarui nama."
      );
    } finally {
      setSavingProfileName(false);
    }
  };

  const toggleNotifs = async () => {
    const nextOpen = !showNotifs;

    // Notification dan Profile tidak boleh terbuka bersamaan.
    setShowProfile(false);
    setShowNotifs(nextOpen);

    if (nextOpen) {
      try {
        const { data } = await apiGet("/notifications");
        setNotifs(data.items);
        setNotificationPage(1);
        await apiPost("/notifications/read", {});
      } catch {
        // Jangan ganggu terminal jika API notifikasi gagal.
      }
    }
  };


  // Tutup dropdown jika user klik di luar Bell/Profile dan panelnya.
  useEffect(() => {
    if (!showNotifs && !showProfile) return;

    const handleOutsideClick = (event) => {
      const target = event.target;

      if (
        target.closest('[data-testid="notif-bell"]') ||
        target.closest('[data-testid="profile-button"]') ||
        target.closest('[data-testid="notif-panel"]') ||
        target.closest('[data-testid="profile-popup"]')
      ) {
        return;
      }

      setShowNotifs(false);
      setShowProfile(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showNotifs, showProfile]);


  const notificationTotalPages = Math.max(
    1,
    Math.ceil((notifs?.length || 0) / NOTIFICATIONS_PER_PAGE)
  );

  const notificationStartIndex =
    (notificationPage - 1) * NOTIFICATIONS_PER_PAGE;

  const visibleNotifications = (notifs || []).slice(
    notificationStartIndex,
    notificationStartIndex + NOTIFICATIONS_PER_PAGE
  );
  const logout = () => {
    localStorage.removeItem("atr_token");
    navigate("/masuk");
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#080b10]">
        <p className="font-mono text-sm text-mute">memuat terminal<span className="blink-cursor text-buy">▌</span></p>
      </div>
    );
  }

  const { account, settings, signals, positions, unread_notifications, user, broker } = data;
  const tabs = user?.role === "admin" ? [...TABS, ["admin", "Admin"]] : TABS;
  const sig = signals.find((s) => s.symbol === symbol) || signals[0];
  const live = sig?.updated_at && (Date.now() - new Date(sig.updated_at).getTime()) < 120000;

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#080b10] text-white" data-testid="terminal-page">
      <header className="fixed inset-x-0 top-0 z-40 w-full border-b border-white/10 bg-[#0b1017]/95 backdrop-blur-xl">
        <div className="mx-auto w-full max-w-[1600px]">
        <div className="flex h-14 items-center justify-between gap-3 px-3 sm:px-5">
          <div className="flex items-center gap-3">
            <img src={branding.logo_url || "/naked-chart-logo.png"} alt={branding.brand_name || "NAKED CHART ID"} className="h-9 w-auto max-w-[72px] object-contain shrink-0" /><div className="min-w-0"><p className="font-display text-[12px] sm:text-sm font-bold leading-none truncate">{branding.brand_name || "NAKED CHART ID"}</p><p className="mt-1 font-mono text-[7px] sm:text-[8px] uppercase tracking-widest text-mute-dim whitespace-nowrap">Read the market. Not the indicators</p></div>
          </div>
          <nav className="hidden items-center gap-1 md:flex">
            {tabs.map(([key, label]) => (
              <button key={key} onClick={() => { setTab(key); setShowNotifs(false); setShowProfile(false); }} data-testid={`nav-tab-${key}`}
                className={`px-3.5 py-2 font-mono text-[11px] font-bold transition-colors ${
                  tab === key ? "border-b-2 border-buy text-white" : "text-mute hover:text-white"
                }`}>
                {label}
                {key === "admin" && supportUnread > 0 && (
                  <span data-testid="nav-admin-unread" className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-sell px-1 font-mono text-[8px] font-bold text-white">{supportUnread}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className={`hidden items-center gap-1.5 font-mono text-[10px] font-bold sm:flex ${live ? "text-buy" : "text-mute-dim"}`} data-testid="live-indicator">
              <span className={`h-2 w-2 rounded-full ${live ? "animate-pulse bg-buy" : "bg-mute-dim"}`} /> LIVE
            </span>
            <span className="hidden font-mono text-[11px] text-mute lg:inline" data-testid="terminal-clock">{fmtClock(now)}</span>
            <button onClick={toggleNotifs} data-testid="notif-bell" className="relative shrink-0 text-mute transition-colors hover:text-white">
              <Bell size={16} />
              {unread_notifications > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-sell font-mono text-[9px] font-bold">
                  {unread_notifications}
                </span>
              )}
            </button>
            <button
              onClick={() => { setShowProfile(!showProfile); setShowNotifs(false); }}
              data-testid="profile-button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-buy/50 bg-buy/10 font-mono text-xs font-bold text-buy transition-colors hover:bg-buy/25"
            >
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </button>
          </div>
        </div>
        <nav className="flex w-full max-w-full gap-1 overflow-x-auto border-t border-white/5 px-3 md:hidden">
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setShowNotifs(false); setShowProfile(false); }}
              className={`whitespace-nowrap px-3 py-2 font-mono text-[10px] font-bold ${tab === key ? "text-buy" : "text-mute"}`}>
              {label}
              {key === "admin" && supportUnread > 0 && (
                <span data-testid="nav-admin-unread-mobile" className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-sell px-1 font-mono text-[8px] font-bold text-white">{supportUnread}</span>
              )}
            </button>
          ))}
        </nav>
        {showNotifs && (
          <div className="absolute right-3 top-14 z-50 w-80 border border-white/15 bg-[#0d1219] shadow-2xl" data-testid="notif-panel">
            <div className="border-b border-white/10 px-4 py-2 font-mono text-[9px] uppercase tracking-widest text-mute-dim">Notifikasi</div>
            <div className="max-h-72 overflow-y-auto">
              {!notifs || notifs.length === 0 ? (
                <p className="px-4 py-6 text-center font-mono text-xs text-mute-dim">belum ada notifikasi</p>
              ) : (
                notifs.map((n) => (
                  <div key={n.id} className="border-b border-white/5 px-4 py-3">
                    <p className="font-mono text-[11px] leading-relaxed text-white">{n.message}</p>
                    <p className="mt-1 font-mono text-[9px] text-mute-dim">{n.created_at ? fmtDateTime(n.created_at) : "—"}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {showProfile && (
          <div className="absolute right-3 top-14 z-50 w-72 border border-white/15 bg-[#0d1219] shadow-2xl" data-testid="profile-popup">
            <div className="flex items-center gap-3 border-b border-white/10 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-buy/50 bg-buy/10 font-display text-base font-bold text-buy">
                {(user?.name || user?.email || "U")[0].toUpperCase()}
              </span>
              <div className="min-w-0">
                <p className="truncate font-mono text-sm font-bold text-white">{user?.name || "Trader"}</p>
                <p className="truncate font-mono text-[10px] text-mute-dim">{user?.email}</p>
              </div>
            </div>
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px]">
              {user?.role !== "admin" && (
              <>
              <div className="flex items-center gap-2">
                <span className="border border-brand/50 bg-brand/10 px-2 py-0.5 font-bold text-[#8b97f5]">TIER {user?.tier || "FREE"}</span>
                {user?.verified ? (
                  <span className="flex items-center gap-1 text-buy" data-testid="verified-badge">
                    <BadgeCheck size={12} /> email terverifikasi
                  </span>
                ) : (
                  <button
                    onClick={async () => {
                      setVerifyOpen(!verifyOpen);
                      if (!verifyOpen) {
                        try {
                          await apiPostRaw("/auth/send-verification", { email: user?.email });
                          toast.success("Kode verifikasi dikirim ke email Anda");
                        } catch { toast.error("Gagal mengirim kode"); }
                      }
                    }}
                    data-testid="verify-email-button"
                    className="flex items-center gap-1 text-warn transition-colors hover:text-white"
                  >
                    <BadgeCheck size={12} /> belum terverifikasi — klik untuk kirim kode
                  </button>
                )}
              </div>
              {verifyOpen && !user?.verified && (
                <div className="mt-2.5 flex gap-1.5">
                  <input
                    value={vcode}
                    onChange={(e) => setVcode(e.target.value)}
                    placeholder="kode 6 digit"
                    maxLength={6}
                    data-testid="verify-code-input"
                    className="h-8 w-28 border border-white/15 bg-ink px-2 font-mono text-xs tracking-[0.3em] outline-none focus:border-buy/60"
                  />
                  <button
                    onClick={async () => {
                      try {
                        await apiPostRaw("/auth/verify", { email: user?.email, code: vcode });
                        toast.success("Email terverifikasi");
                        setVerifyOpen(false);
                        setVcode("");
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Kode salah");
                      }
                    }}
                    data-testid="verify-submit"
                    className="border border-buy bg-buy/10 px-3 font-mono text-[10px] font-bold text-buy transition-colors hover:bg-buy hover:text-ink"
                  >
                    OK
                  </button>
                </div>
              )}
              </>
              )}
            </div>
            <div className="p-2">
              <button onClick={() => { setTab("profile"); setShowProfile(false); }} data-testid="profile-account"
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left font-mono text-xs text-mute transition-colors hover:bg-white/5 hover:text-white">
                <User size={14} /> Profil & Akun
              </button>
              <button onClick={() => { setTab("security"); setShowProfile(false); }} data-testid="profile-security"
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left font-mono text-xs text-mute transition-colors hover:bg-white/5 hover:text-white">
                <LockKeyhole size={14} /> Keamanan
              </button>
              <button onClick={() => { setTab("notifications"); setShowProfile(false); }} data-testid="profile-notifications"
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left font-mono text-xs text-mute transition-colors hover:bg-white/5 hover:text-white">
                <Bell size={14} /> Notifikasi
              </button>
              <button onClick={() => {
                setShowProfile(false);
                window.dispatchEvent(new Event("open-support"));
              }} data-testid="profile-help"
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left font-mono text-xs text-mute transition-colors hover:bg-white/5 hover:text-white">
                <LifeBuoy size={14} /> Bantuan & Support
              </button>
              <button onClick={logout} data-testid="profile-logout"
                className="mt-1 flex w-full items-center gap-3 border-t border-white/10 px-3 py-2.5 text-left font-mono text-xs font-bold text-sell transition-colors hover:bg-sell/10">
                <LogOut size={14} /> Logout
              </button>
            </div>
          </div>
        )}
        </div>
      </header>

      <main className="terminal-main mx-auto w-full max-w-[1600px] p-3 sm:p-5">
        <SourceBar signals={signals} />
        {user?.role !== "admin" && <AccessBar user={user} now={now} />}
        {tab === "dashboard" && (
          <div className="grid min-w-0 gap-3 xl:grid-cols-12" data-testid="dashboard-tab">
            <div className="min-w-0 space-y-3 xl:col-span-3">
              <OverviewPanel sig={sig} symbol={symbol} onSelect={setSymbol} symbols={SYMBOL_LIST} />
              <StructurePanel sig={sig} />
              <LiquidityPanel sig={sig} />
            </div>
            <div className="min-w-0 space-y-3 xl:col-span-6">
              <CandleChart symbol={symbol} height={380} />
              <SetupCard sig={sig} balance={account?.balance} riskPct={settings?.risk_pct} onViewChart={() => setTab("execution")} />
            </div>
            <div className="min-w-0 xl:col-span-3">
              <LiveMonitor />
            </div>
            <div className="min-w-0 xl:col-span-7">
              <WatchlistTable signals={signals} selected={symbol} onSelect={setSymbol} />
            </div>
            <div className="min-w-0 xl:col-span-5">
              <AlertsTable />
            </div>
          </div>
        )}

        {tab === "execution" && (
          <ExecutionLab symbol={symbol} />
        )}

        {tab === "chart" && (
          <div className="grid min-w-0 gap-3 xl:grid-cols-12" data-testid="chart-tab">
            <div className="min-w-0 xl:col-span-8">
              <CandleChart symbol={symbol} height={560} />
            </div>
            <div className="min-w-0 space-y-3 xl:col-span-4">
              <OverviewPanel sig={sig} symbol={symbol} onSelect={setSymbol} symbols={SYMBOL_LIST} />
              <SetupCard sig={sig} balance={account?.balance} riskPct={settings?.risk_pct} onViewChart={() => {}} />
              <StructurePanel sig={sig} />
            </div>
          </div>
        )}



        {tab === "liquidity-flow" && (
          <LiquidityFlow />
        )}


        {tab === "scanner" && (
          user?.role === "user" && !user?.access ? (
            <div className="border border-warn/30 bg-warn/5 p-10 text-center" data-testid="scanner-locked">
              <p className="font-display text-lg font-bold text-white">Akses Sinyal Terkunci</p>
              <p className="mx-auto mt-3 max-w-md font-mono text-xs leading-relaxed text-mute">
                Verifikasi email Anda lewat ikon profil di kanan atas untuk membuka trial gratis 3 hari.
                Jika masa akses habis atau akun dikunci admin, hubungi admin untuk aktivasi.
              </p>
              <button onClick={() => window.dispatchEvent(new Event("open-support"))} data-testid="scanner-contact-admin"
                className="mx-auto mt-5 flex h-9 items-center gap-2 border border-buy bg-buy/10 px-4 font-mono text-[11px] font-bold uppercase tracking-wider text-buy transition-colors hover:bg-buy hover:text-ink">
                Hubungi Admin
              </button>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="scanner-tab">
              {(settings?.watch_symbols?.length
                ? signals.filter((s) => settings.watch_symbols.includes(s.symbol))
                : signals
              ).filter((s) => !s.admin_off).map((s) => (
                <SignalCard key={s.symbol} sig={s} balance={account?.balance} riskPct={settings?.risk_pct} />
              ))}
            </div>
          )
        )}

        {tab === "learning" && (
          <LearningPage />
        )}

        {tab === "history" && (
          <div className="space-y-5" data-testid="history-tab">
            <HistoryPage />
            <div className="border border-white/10 bg-[#0d1219] p-4">
              <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">Posisi Terbuka (Paper)</p>
              <PositionsPanel positions={positions} onChanged={load} />
            </div>
          </div>
        )}

        {tab === "pnl" && (
          <div data-testid="pnl-tab">
            <PnlPage />
          </div>
        )}

        {tab === "news" && (
          <div data-testid="news-tab">
            <NewsPage />
          </div>
        )}

        {tab === "admin" && user?.role === "admin" && (
          <div data-testid="admin-tab">
            <AdminTab />
          </div>
        )}

        {tab === "profile" && (
          <div className="space-y-5" data-testid="profile-tab">
            <div className="border border-white/10 bg-[#0d1219] p-5">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Profil & Akun</h2>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">Informasi akun NakedChart</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="border border-white/10 bg-black/10 p-4">
                  <label className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">
                    Nama
                  </label>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      maxLength={100}
                      className="min-w-0 flex-1 border border-white/10 bg-[#0a0f15] px-3 py-2 text-sm text-white outline-none placeholder:text-mute-dim focus:border-white/25"
                      placeholder="Masukkan nama"
                      disabled={savingProfileName}
                    />
                    <button
                      type="button"
                      onClick={saveProfileName}
                      disabled={savingProfileName || !profileName.trim()}
                      className="border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-300 transition hover:bg-emerald-400/15 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {savingProfileName ? "Menyimpan..." : "Simpan Nama"}
                    </button>
                  </div>
                </div>

                <div className="border border-white/10 bg-black/10 p-4">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">Email</p>
                  <p className="mt-2 break-all text-sm text-white">{user?.email || "-"}</p>
                  <p className="mt-1 text-[10px] text-mute-dim">Email akun tidak dapat diubah.</p>
                </div>

                <div className="border border-white/10 bg-black/10 p-4">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">Role</p>
                  <p className="mt-2 text-sm uppercase text-white">{user?.role || "-"}</p>
                </div>

                <div className="border border-white/10 bg-black/10 p-4">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">Tier</p>
                  <p className="mt-2 text-sm uppercase text-white">{user?.tier || "-"}</p>
                </div>
              </div>

              {user?.role !== "admin" && (
                <div className="mt-4 flex items-center justify-between border border-white/10 bg-black/10 p-4">
                  <div>
                    <p className="text-sm text-white">Status Verifikasi Email</p>
                    <p className="mt-1 text-[11px] text-mute-dim">Status verifikasi akun kamu.</p>
                  </div>
                  <span className={`border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${user?.verified ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-amber-400/30 bg-amber-400/10 text-amber-300"}`}>
                    {user?.verified ? "Terverifikasi" : "Belum Terverifikasi"}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "security" && (
          <div className="space-y-5" data-testid="security-tab">
            <div className="border border-white/10 bg-[#0d1219] p-5">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5">
                  <LockKeyhole className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Keamanan</h2>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">Kelola keamanan akun</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between border border-white/10 bg-black/10 p-4">
                  <div>
                    <p className="text-sm text-white">Password</p>
                    <p className="mt-1 text-[11px] text-mute-dim">Ubah password akun secara berkala.</p>
                  </div>
                  <span className="border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-mute-dim">Segera</span>
                </div>

                <div className="flex items-center justify-between border border-white/10 bg-black/10 p-4">
                  <div>
                    <p className="text-sm text-white">Two-Factor Authentication</p>
                    <p className="mt-1 text-[11px] text-mute-dim">Tambahkan lapisan keamanan tambahan untuk login.</p>
                  </div>
                  <span className="border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-mute-dim">Segera</span>
                </div>

                <div className="flex items-center justify-between border border-white/10 bg-black/10 p-4">
                  <div>
                    <p className="text-sm text-white">Sesi Login</p>
                    <p className="mt-1 text-[11px] text-mute-dim">Kontrol sesi aktif dan perangkat yang digunakan.</p>
                  </div>
                  <span className="border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-mute-dim">Segera</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "notifications" && (
          <div className="space-y-5" data-testid="notifications-tab">
            <div className="border border-white/10 bg-[#0d1219] p-5">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5">
                  <Bell className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Notifikasi</h2>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">Riwayat pemberitahuan akun</p>
                </div>
              </div>

              {notifs?.length ? (
                <>
                  <div className="space-y-2">
                    {visibleNotifications.map((n, i) => (
                      <div key={n?.id || n?._id || i} className="border border-white/10 bg-black/10 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm text-white">{n?.title || n?.message || "Notifikasi"}</p>
                            {n?.message && n?.title && (
                              <p className="mt-1 text-[11px] text-mute-dim">{n.message}</p>
                            )}
                          </div>
                          {n?.created_at && (
                            <span className="shrink-0 font-mono text-[9px] text-mute-dim">{n.created_at}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={() => setNotificationPage((p) => Math.max(1, p - 1))}
                      disabled={notificationPage <= 1}
                      className="border border-white/10 bg-black/10 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-white transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      ← Sebelumnya
                    </button>

                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-mute-dim">
                      Halaman {notificationPage} / {notificationTotalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() => setNotificationPage((p) => Math.min(notificationTotalPages, p + 1))}
                      disabled={notificationPage >= notificationTotalPages}
                      className="border border-white/10 bg-black/10 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-white transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      Berikutnya →
                    </button>
                  </div>
                </>
              ) : (
                <div className="border border-white/10 bg-black/10 p-6 text-center">
                  <p className="text-sm text-mute-dim">Belum ada notifikasi.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div className="grid gap-4 xl:grid-cols-2" data-testid="settings-tab">
            <SettingsPanel settings={settings} onSaved={load} broker={broker} activeSymbols={data.active_symbols} />
            <div className="space-y-4">
              <PushPanel />
              {user?.role !== "admin" && <PlansPanel user={user} />}
              <BrokerPanel broker={broker} onChanged={load} />
              {user?.role === "admin" && <TelegramPanel settings={settings} onChanged={load} />}
            </div>
          </div>
        )}

        <div className="mt-6 border-t border-white/10 pt-4 font-mono text-[10px] text-mute-dim">
          <span>Analysis & Scanner aktif · Market data aktif</span>
        </div>
      </main>
      <SupportChat isAdmin={user?.role === "admin"} />
    </div>
  );
}
